import React from 'react'
import { Table, TableColumnsType } from 'antd'
import { IMovementRS } from '../../../models/salesModel'
import { textNumberFormater } from '../../../utils'
import { getTopProducts, calculateSummary, TProductRanking } from '../../../utils/reportUtils'
import { TExcelColumn } from '../../../utils/excelExport'
import ReportSummaryCards, { TSummaryCard } from './ReportSummaryCards'
import {
  DollarOutlined,
  TrophyOutlined,
  ShoppingOutlined,
  BarChartOutlined
} from '@ant-design/icons'

type TopProductsReportProps = {
  data: IMovementRS[]
  loading?: boolean
  limit?: number
}

function TopProductsReport({ data, loading, limit = 50 }: Readonly<TopProductsReportProps>) {
  const products = getTopProducts(data, limit)
  const summary = calculateSummary(data)
  const topProduct = products[0]

  const cards: TSummaryCard[] = [
    {
      title: 'Total Vendido',
      value: summary.totalAmount,
      format: 'money',
      color: '#52c41a',
      prefix: <DollarOutlined />
    },
    {
      title: 'Productos Únicos',
      value: products.length,
      format: 'number',
      color: '#1890ff',
      prefix: <ShoppingOutlined />
    },
    {
      title: 'Producto Top',
      value: topProduct?.itemName || '-',
      format: 'text',
      color: '#faad14',
      prefix: <TrophyOutlined />
    },
    {
      title: 'Unidades Top',
      value: topProduct?.totalQuantity || 0,
      format: 'number',
      color: '#722ed1',
      prefix: <BarChartOutlined />
    }
  ]

  const columns: TableColumnsType = [
    { title: '#', dataIndex: 'rank', width: 50 },
    { title: 'Código', dataIndex: 'itemCode', width: 120 },
    { title: 'Producto', dataIndex: 'itemName', width: 220 },
    {
      title: 'Unidades Vendidas',
      dataIndex: 'totalQuantity',
      width: 140,
      align: 'center',
      sorter: (a: any, b: any) => a.totalQuantity - b.totalQuantity,
      defaultSortOrder: 'descend'
    },
    {
      title: 'Precio Promedio',
      dataIndex: 'averagePrice',
      width: 140,
      align: 'right',
      render: (text) => <span className="numeric-values-data-table">{textNumberFormater(text)}</span>,
      sorter: (a: any, b: any) => a.averagePrice - b.averagePrice
    },
    {
      title: 'Total Vendido',
      dataIndex: 'totalAmount',
      width: 150,
      align: 'right',
      render: (text) => <span className="numeric-values-data-table" style={{ fontWeight: 600 }}>{textNumberFormater(text)}</span>,
      sorter: (a: any, b: any) => a.totalAmount - b.totalAmount
    },
    {
      title: 'Impuestos',
      dataIndex: 'totalTaxes',
      width: 120,
      align: 'right',
      render: (text) => <span className="numeric-values-data-table">{textNumberFormater(text)}</span>
    },
    {
      title: 'Descuentos',
      dataIndex: 'totalDiscounts',
      width: 120,
      align: 'right',
      render: (text) => <span className="numeric-values-data-table">{textNumberFormater(text)}</span>
    },
    {
      title: 'Transacciones',
      dataIndex: 'transactionCount',
      width: 120,
      align: 'center',
      sorter: (a: any, b: any) => a.transactionCount - b.transactionCount
    }
  ]

  const tableData = products.map((el, i) => ({
    ...el,
    rank: i + 1,
    key: el.key
  }))

  return (
    <div>
      <ReportSummaryCards cards={cards} loading={loading} />
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={tableData.length > 15 ? { pageSize: 15, showTotal: (total) => `Total ${total} productos` } : false}
        scroll={{ x: 1100 }}
        size="small"
        loading={loading}
      />
    </div>
  )
}

export const productExcelColumns: TExcelColumn[] = [
  { header: '#', key: 'rank', width: 6 },
  { header: 'Código', key: 'itemCode', width: 15 },
  { header: 'Producto', key: 'itemName', width: 30 },
  { header: 'Unidades Vendidas', key: 'totalQuantity', width: 18, format: 'number' },
  { header: 'Precio Promedio', key: 'averagePrice', width: 16, format: 'money' },
  { header: 'Total Vendido', key: 'totalAmount', width: 18, format: 'money' },
  { header: 'Impuestos', key: 'totalTaxes', width: 15, format: 'money' },
  { header: 'Descuentos', key: 'totalDiscounts', width: 15, format: 'money' },
  { header: 'Transacciones', key: 'transactionCount', width: 14, format: 'number' }
]

export function getProductExcelData(data: IMovementRS[], limit: number = 50): (TProductRanking & { rank: number })[] {
  return getTopProducts(data, limit).map((el, i) => ({
    ...el,
    rank: i + 1
  }))
}

export default TopProductsReport
